import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { ACTIVE_PAGE_REFRESH_INTERVAL_MS } from "../lib/activePageRefresh";
import type { CustomerId, FacilityId } from "../lib/workIds";
import type { WorkRequestFilters } from "./useWorkRequestsQuery";

const newWorkRequestCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});

async function fetchNewWorkRequestCount(
  customerId: CustomerId,
  facilityId: FacilityId,
  filters: WorkRequestFilters,
  newerThanId: string,
): Promise<number> {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ].requests["new-count"].$get({
    param: { customerId, facilityId },
    query: {
      view: filters.view,
      newerThanId: String(newerThanId),
      ...(filters.search.trim() ? { search: filters.search.trim() } : {}),
      ...(filters.priorities.length
        ? { priorities: filters.priorities.join(",") }
        : {}),
      ...(filters.submittedFrom
        ? { submittedFrom: filters.submittedFrom }
        : {}),
      ...(filters.submittedTo ? { submittedTo: filters.submittedTo } : {}),
      ...(filters.requestedBy.length
        ? { requestedBy: filters.requestedBy.join(",") }
        : {}),
      ...(filters.statuses.length
        ? { statuses: filters.statuses.join(",") }
        : {}),
      ...(filters.tabStatus ? { tabStatus: filters.tabStatus } : {}),
      ...(filters.assetAssignment
        ? { assetAssignment: filters.assetAssignment }
        : {}),
      ...(filters.assetIds.length
        ? { assetIds: filters.assetIds.join(",") }
        : {}),
    },
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch the new Work Request count: ${response.status}`,
    );
  }
  const payload = newWorkRequestCountResponseSchema.parse(
    await response.json(),
  );
  return payload.count;
}

/**
 * Polls a cheap count of eligible Work Requests created after the newest one
 * the caller already has (IWA-18987) — the Requests list itself is no longer
 * polled; this tells the UI when it's worth offering a manual refresh.
 */
export function useNewWorkRequestCountQuery(
  filters: WorkRequestFilters,
  newerThanId: string | undefined,
) {
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useQuery({
    queryKey: [
      "work-requests-new-count",
      selectedCustomer?.id,
      selectedFacility?.id,
      filters,
      newerThanId,
    ],
    queryFn: () => {
      if (!selectedCustomer || !selectedFacility || newerThanId === undefined) {
        throw new Error(
          "A customer, facility, and existing Work Request must be present.",
        );
      }
      return fetchNewWorkRequestCount(
        selectedCustomer.id,
        selectedFacility.id,
        filters,
        newerThanId,
      );
    },
    enabled:
      Boolean(selectedCustomer && selectedFacility) &&
      newerThanId !== undefined,
    refetchInterval: ACTIVE_PAGE_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
  });
}
